import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  InvoiceNote,
  createInvoiceNote,
  deleteInvoiceNote,
  listInvoiceNotes,
  updateInvoiceNote,
} from '@/services/invoiceNotes'

const notesKey = (invoiceId: string) => ['invoice-notes', invoiceId] as const

function invalidateInvoiceLists(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries(['invoices'])
}

export function useInvoiceNotes(invoiceId: string | null) {
  return useQuery<InvoiceNote[]>(
    notesKey(invoiceId ?? ''),
    () => listInvoiceNotes(invoiceId as string),
    { enabled: !!invoiceId }
  )
}

export function useCreateInvoiceNote(invoiceId: string) {
  const qc = useQueryClient()
  return useMutation(
    (body: string) => createInvoiceNote(invoiceId, body),
    {
      onSuccess: () => {
        qc.invalidateQueries(notesKey(invoiceId))
        invalidateInvoiceLists(qc)
      },
    }
  )
}

export function useUpdateInvoiceNote(invoiceId: string) {
  const qc = useQueryClient()
  return useMutation(
    ({ noteId, body }: { noteId: string; body: string }) =>
      updateInvoiceNote(noteId, body),
    {
      onSuccess: () => {
        qc.invalidateQueries(notesKey(invoiceId))
        invalidateInvoiceLists(qc)
      },
    }
  )
}

export function useDeleteInvoiceNote(invoiceId: string) {
  const qc = useQueryClient()
  return useMutation((noteId: string) => deleteInvoiceNote(noteId), {
    onSuccess: () => {
      qc.invalidateQueries(notesKey(invoiceId))
      invalidateInvoiceLists(qc)
    },
  })
}
